import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE } from '../../config';
import ConfirmModal from '../ui/ConfirmModal';

const TABS = ['Overview', 'Users', 'Listings', 'Transactions', 'Chats'];

function StatCard({ label, value, sub }) {
  return (
    <div style={{
      background: '#fff', borderRadius: 12, padding: '20px 24px',
      boxShadow: '0 1px 6px rgba(0,0,0,0.08)', minWidth: 140, flex: 1
    }}>
      <div style={{ fontSize: 28, fontWeight: 700, color: '#202020' }}>{value ?? '—'}</div>
      <div style={{ fontSize: 13, color: 'var(--text-soft)', marginTop: 4 }}>{label}</div>
      {sub && <div style={{ fontSize: 12, color: '#999', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function SimpleBarChart({ data, color = 'var(--maroon)' }) {
  if (!data || !data.length) return <p style={{ color: '#999', fontSize: 13 }}>No data yet</p>;
  const max = Math.max(...data.map(d => d.count), 1);
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 80, marginTop: 8 }}>
      {data.map((d, i) => (
        <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <div title={`${d._id}: ${d.count}`} style={{
            width: '100%', height: `${(d.count / max) * 70}px`,
            background: color, borderRadius: 3, minHeight: 2
          }} />
          <div style={{ fontSize: 9, color: '#aaa', marginTop: 2, writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>
            {d._id?.slice(5)}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Overview Tab ─────────────────────────────────────────────
function OverviewTab({ analytics }) {
  if (!analytics) return <p>Loading...</p>;
  const { users, listings, messaging, transactions, growth } = analytics;
  const g = growth?.last30days || {};
  return (
    <div>
      <h3 style={{ margin: '0 0 16px' }}>Platform Summary</h3>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 24 }}>
        <StatCard label="Total Users" value={users?.total} sub={`+${users?.newToday || 0} today`} />
        <StatCard label="Weekly Active" value={users?.wau} sub="last 7 days" />
        <StatCard label="Monthly Active" value={users?.mau} sub="last 30 days" />
        <StatCard label="Active Listings" value={listings?.active} />
        <StatCard label="Transactions Done" value={transactions?.completed} sub={`${transactions?.completionRate || 0}% rate`} />
        <StatCard label="Total Messages" value={messaging?.totalMessages} sub={`${messaging?.messagesLast24h || 0} today`} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 20 }}>
        <div style={{ background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 6px rgba(0,0,0,0.08)' }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#202020' }}>Daily Signups (30d)</div>
          <SimpleBarChart data={g.signups} color="var(--maroon)" />
        </div>
        <div style={{ background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 6px rgba(0,0,0,0.08)' }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#202020' }}>Listings Created (30d)</div>
          <SimpleBarChart data={g.listingsCreated} color="#10b981" />
        </div>
        <div style={{ background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 6px rgba(0,0,0,0.08)' }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#202020' }}>Messages Sent (30d)</div>
          <SimpleBarChart data={g.messagesPerDay} color="#f59e0b" />
        </div>
        <div style={{ background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 6px rgba(0,0,0,0.08)' }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#202020' }}>Transactions Completed (30d)</div>
          <SimpleBarChart data={g.transactionsPerDay} color="#ef4444" />
        </div>
      </div>

      <div style={{ marginTop: 24, background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 6px rgba(0,0,0,0.08)' }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#202020', marginBottom: 12 }}>Listings by Category</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {(listings?.byCategory || []).map(c => (
            <span key={c._id} style={{
              background: '#f3f4f6', borderRadius: 20, padding: '4px 12px', fontSize: 13
            }}>
              {c._id}: <strong>{c.count}</strong>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Users Tab ────────────────────────────────────────────────
function UsersTab({ refresh }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState({ open: false, id: null, email: '' });

  const load = useCallback(async () => {
    setLoading(true);
    const res = await fetch(`${API_BASE}/admin/users`, { credentials: 'include' });
    if (res.ok) setUsers(await res.json());
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const suspend = async (id, suspended) => {
    const path = suspended ? 'unsuspend' : 'suspend';
    await fetch(`${API_BASE}/admin/users/${id}/${path}`, { method: 'POST', credentials: 'include' });
    load();
  };

  const deleteUser = (id, email) => {
    setConfirmDelete({ open: true, id, email });
  };

  const executeDeleteUser = async () => {
    await fetch(`${API_BASE}/admin/users/${confirmDelete.id}`, { method: 'DELETE', credentials: 'include' });
    setConfirmDelete({ open: false, id: null, email: '' });
    load();
  };

  if (loading) return <p>Loading users...</p>;

  return (
    <div>
      <h3 style={{ margin: '0 0 16px' }}>Users ({users.length})</h3>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ background: '#f9fafb', textAlign: 'left' }}>
            {['Name', 'Email', 'Role', 'Campus', 'Listings', 'Transactions', 'Joined', 'Status', 'Actions'].map(h => (
              <th key={h} style={{ padding: '10px 12px', borderBottom: '1px solid #e5e7eb', fontWeight: 600 }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {users.map(u => (
            <tr key={u._id} style={{ borderBottom: '1px solid #f3f4f6' }}>
              <td style={{ padding: '10px 12px' }}>{u.displayName}</td>
              <td style={{ padding: '10px 12px', color: '#555' }}>{u.email}</td>
              <td style={{ padding: '10px 12px' }}>{u.role || '—'}</td>
              <td style={{ padding: '10px 12px' }}>{u.campus || '—'}</td>
              <td style={{ padding: '10px 12px', textAlign: 'center' }}>{u.listingCount}</td>
              <td style={{ padding: '10px 12px', textAlign: 'center' }}>{u.numTransactions || 0}</td>
              <td style={{ padding: '10px 12px', color: '#888' }}>{new Date(u.createdAt).toLocaleDateString()}</td>
              <td style={{ padding: '10px 12px' }}>
                <span style={{
                  padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600,
                  background: u.isSuspended ? '#fee2e2' : '#d1fae5',
                  color: u.isSuspended ? '#b91c1c' : '#065f46'
                }}>
                  {u.isSuspended ? 'Suspended' : 'Active'}
                </span>
              </td>
              <td style={{ padding: '10px 12px', display: 'flex', gap: 6 }}>
                <button onClick={() => suspend(u._id, u.isSuspended)} style={{
                  padding: '4px 10px', fontSize: 11, borderRadius: 6, cursor: 'pointer',
                  background: u.isSuspended ? '#d1fae5' : '#fef3c7', border: 'none',
                  color: u.isSuspended ? '#065f46' : '#92400e'
                }}>
                  {u.isSuspended ? 'Unsuspend' : 'Suspend'}
                </button>
                <button onClick={() => deleteUser(u._id, u.email)} style={{
                  padding: '4px 10px', fontSize: 11, borderRadius: 6, cursor: 'pointer',
                  background: '#fee2e2', border: 'none', color: '#b91c1c'
                }}>
                  Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <ConfirmModal
        open={confirmDelete.open}
        title={`Delete user?`}
        message={`Delete ${confirmDelete.email}? This is irreversible and removes all their data.`}
        confirmLabel="Delete User"
        danger
        onConfirm={executeDeleteUser}
        onCancel={() => setConfirmDelete({ open: false, id: null, email: '' })}
      />
    </div>
  );
}

// ── Listings Tab ─────────────────────────────────────────────
function ListingsTab() {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmRemove, setConfirmRemove] = useState({ open: false, id: null });

  const load = useCallback(async () => {
    setLoading(true);
    const res = await fetch(`${API_BASE}/admin/listings`, { credentials: 'include' });
    if (res.ok) setListings(await res.json());
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const hold = async (id, onHold) => {
    const path = onHold ? 'release' : 'hold';
    await fetch(`${API_BASE}/admin/listings/${id}/${path}`, { method: 'POST', credentials: 'include' });
    load();
  };

  const remove = (id) => {
    setConfirmRemove({ open: true, id });
  };

  const executeRemove = async () => {
    await fetch(`${API_BASE}/admin/listings/${confirmRemove.id}`, { method: 'DELETE', credentials: 'include' });
    setConfirmRemove({ open: false, id: null });
    load();
  };

  if (loading) return <p>Loading listings...</p>;

  return (
    <div>
      <h3 style={{ margin: '0 0 16px' }}>Listings ({listings.length})</h3>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ background: '#f9fafb', textAlign: 'left' }}>
            {['Title', 'Category', 'Owner', 'Status', 'Hold', 'Item ID', 'Created', 'Actions'].map(h => (
              <th key={h} style={{ padding: '10px 12px', borderBottom: '1px solid #e5e7eb', fontWeight: 600 }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {listings.map(l => (
            <tr key={l._id} style={{ borderBottom: '1px solid #f3f4f6' }}>
              <td style={{ padding: '10px 12px' }}>{l.title}</td>
              <td style={{ padding: '10px 12px', color: '#555' }}>{l.category}</td>
              <td style={{ padding: '10px 12px', color: '#555' }}>{l.ownerId?.displayName || '—'}</td>
              <td style={{ padding: '10px 12px' }}>
                <span style={{
                  padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600,
                  background: l.status === 'available' ? '#d1fae5' : '#f3f4f6',
                  color: l.status === 'available' ? '#065f46' : '#555'
                }}>
                  {l.status}
                </span>
              </td>
              <td style={{ padding: '10px 12px' }}>
                {l.isOnHold && (
                  <span style={{ fontSize: 11, color: '#b91c1c' }} title={l.holdReason}>🔒 {l.holdReason?.slice(0, 30)}</span>
                )}
              </td>
              <td style={{ padding: '10px 12px', fontFamily: 'monospace', color: '#888' }}>{l.itemId}</td>
              <td style={{ padding: '10px 12px', color: '#888' }}>{new Date(l.createdAt).toLocaleDateString()}</td>
              <td style={{ padding: '10px 12px', display: 'flex', gap: 6 }}>
                <button onClick={() => hold(l._id, l.isOnHold)} style={{
                  padding: '4px 10px', fontSize: 11, borderRadius: 6, cursor: 'pointer',
                  background: l.isOnHold ? '#d1fae5' : '#fef3c7', border: 'none',
                  color: l.isOnHold ? '#065f46' : '#92400e'
                }}>
                  {l.isOnHold ? 'Release' : 'Hold'}
                </button>
                <button onClick={() => remove(l._id)} style={{
                  padding: '4px 10px', fontSize: 11, borderRadius: 6, cursor: 'pointer',
                  background: '#fee2e2', border: 'none', color: '#b91c1c'
                }}>
                  Remove
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <ConfirmModal
        open={confirmRemove.open}
        title="Remove listing?"
        message="This listing will be removed from the platform."
        confirmLabel="Remove"
        danger
        onConfirm={executeRemove}
        onCancel={() => setConfirmRemove({ open: false, id: null })}
      />
    </div>
  );
}

// ── Transactions Tab ──────────────────────────────────────────
function TransactionsTab() {
  const [data, setData] = useState(null);
  const [view, setView] = useState('completed');

  useEffect(() => {
    fetch(`${API_BASE}/admin/transactions`, { credentials: 'include' })
      .then(r => r.json()).then(setData);
  }, []);

  if (!data) return <p>Loading...</p>;
  const rows = view === 'completed' ? data.completed : data.expired;

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {['completed', 'expired'].map(v => (
          <button key={v} onClick={() => setView(v)} style={{
            padding: '6px 16px', borderRadius: 20, border: 'none', cursor: 'pointer', fontSize: 13,
            background: view === v ? 'var(--maroon)' : '#f3f4f6', color: view === v ? '#fff' : '#202020'
          }}>
            {v === 'completed' ? `Completed (${data.completed.length})` : `Expired (${data.expired.length})`}
          </button>
        ))}
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ background: '#f9fafb', textAlign: 'left' }}>
            {['Listing', 'Category', 'Buyer', 'Seller', 'Date'].map(h => (
              <th key={h} style={{ padding: '10px 12px', borderBottom: '1px solid #e5e7eb', fontWeight: 600 }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(t => (
            <tr key={t._id} style={{ borderBottom: '1px solid #f3f4f6' }}>
              <td style={{ padding: '10px 12px' }}>{t.listing?.title || '—'}</td>
              <td style={{ padding: '10px 12px', color: '#888' }}>{t.listing?.category || '—'}</td>
              <td style={{ padding: '10px 12px' }}>{t.buyer?.displayName || t.buyer?.email || '—'}</td>
              <td style={{ padding: '10px 12px' }}>{t.seller?.displayName || t.seller?.email || '—'}</td>
              <td style={{ padding: '10px 12px', color: '#888' }}>{new Date(t.updatedAt).toLocaleDateString()}</td>
            </tr>
          ))}
          {!rows.length && (
            <tr><td colSpan={5} style={{ padding: 16, color: '#999', textAlign: 'center' }}>No {view} transactions yet</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ── Chats Tab ────────────────────────────────────────────────
function ChatsTab() {
  const [data, setData] = useState(null);
  useEffect(() => {
    fetch(`${API_BASE}/admin/chats`, { credentials: 'include' })
      .then(r => r.json()).then(setData);
  }, []);
  if (!data) return <p>Loading...</p>;
  return (
    <div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 24 }}>
        <StatCard label="Total Chat Rooms" value={data.totalRooms} />
        <StatCard label="Total Messages" value={data.totalMessages} />
      </div>
      <h4 style={{ margin: '0 0 12px', color: '#202020' }}>Most Active Users</h4>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ background: '#f9fafb', textAlign: 'left' }}>
            {['User', 'Email', 'Messages Sent'].map(h => (
              <th key={h} style={{ padding: '10px 12px', borderBottom: '1px solid #e5e7eb', fontWeight: 600 }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {(data.topSenders || []).map((s, i) => (
            <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
              <td style={{ padding: '10px 12px' }}>{s.displayName || '—'}</td>
              <td style={{ padding: '10px 12px', color: '#888' }}>{s.email || '—'}</td>
              <td style={{ padding: '10px 12px', fontWeight: 600 }}>{s.count}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Main AdminDashboard ───────────────────────────────────────
export default function AdminDashboard({ user }) {
  const [activeTab, setActiveTab] = useState('Overview');
  const [analytics, setAnalytics] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch(`${API_BASE}/admin/analytics`, { credentials: 'include' })
      .then(r => {
        if (!r.ok) throw new Error('Access denied');
        return r.json();
      })
      .then(setAnalytics)
      .catch(e => setError(e.message));
  }, []);

  if (error) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <h2 style={{ color: '#b91c1c' }}>Access Denied</h2>
        <p style={{ color: 'var(--text-soft)' }}>{error}</p>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--surface-bg)', padding: 'clamp(16px, 4vw, 24px) clamp(16px, 5vw, 32px)', fontFamily: 'var(--font)' }}>
      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ margin: 0, fontSize: 26, fontWeight: 700, color: 'var(--maroon)' }}>UTrade Admin</h1>
        <p style={{ margin: '4px 0 0', color: 'var(--text-soft)', fontSize: 14 }}>Logged in as {user?.email}</p>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 24, borderBottom: '1px solid #e4e3e2', paddingBottom: 0, overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
        {TABS.map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)} style={{
            padding: '10px 20px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 14, flexShrink: 0, whiteSpace: 'nowrap',
            fontWeight: activeTab === tab ? 700 : 400,
            fontFamily: 'var(--font-heading)',
            color: activeTab === tab ? 'var(--maroon)' : 'var(--text-soft)',
            borderBottom: activeTab === tab ? '2px solid var(--maroon)' : '2px solid transparent',
            marginBottom: -1, transition: 'all 0.15s'
          }}>
            {tab}
          </button>
        ))}
      </div>

      {/* Tab content — wide tables scroll horizontally on small screens */}
      <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
        {activeTab === 'Overview' && <OverviewTab analytics={analytics} />}
        {activeTab === 'Users' && <UsersTab />}
        {activeTab === 'Listings' && <ListingsTab />}
        {activeTab === 'Transactions' && <TransactionsTab />}
        {activeTab === 'Chats' && <ChatsTab />}
      </div>
    </div>
  );
}
